import {
  ChakraFlex,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Icon,
  FAIconName,
  Text,
} from '@equip.health/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';

import { WEEK_VIEW_ITEM_HEIGHT } from '~/lib/constants/calendar.constants';
import { formatISOToHourAndMinute } from '~/lib/util/calendar.util';
import { getSlotOffsetInPx } from '~/lib/util/schedule/weekViewLayout.util';

const TOP_LEVEL_ID = 'week-event-item';
const { primary } = EquipUIFireflyV2Theme.colors;
const { label, labelBold } = EquipUITheme.typography;

interface ScheduleWeekEventItemProps {
  /** Horizontal offset within the day column, in %, from the day level layout */
  leftPercent: number;
  onScheduleItemClicked: (externalId: string) => void;
  scheduledItem: ScheduleItemExtended;
}

const ScheduleWeekEventItem = ({
  leftPercent,
  onScheduleItemClicked,
  scheduledItem,
}: ScheduleWeekEventItemProps) => {
  const {
    viewCancelledAppointmentsInMaud: isViewCancelledAppointmentsInMaudEnabled,
  } = useFlags();

  const {
    color,
    border,
    duration,
    externalId,
    startDateTime: start,
    title,
    width,
    isNew,
    isCancelled,
  } = scheduledItem;

  const eventBoxProps: Record<string, string> = {
    alignItems: 'center',
    background: isNew ? color : `${color}.10`,
    border: `1px solid ${isNew ? border : primary[10]}`,
    borderRadius: '4px',
    height: `${(duration / 30) * WEEK_VIEW_ITEM_HEIGHT}px`,
    left: `${leftPercent}%`,
    marginTop: `${getSlotOffsetInPx(start, WEEK_VIEW_ITEM_HEIGHT)}px`,
    overflow: 'hidden',
    padding: '4px 8px',
    position: 'absolute',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: `${width}%`,
    zIndex: '1',
  };

  const titleTimePart = `, ${formatISOToHourAndMinute(start)} - ${formatISOToHourAndMinute(
    DateTime.fromISO(start).plus({ minutes: duration }).toISO(),
  )}`;

  return (
    <ChakraFlex
      _hover={{ backgroundColor: `${color}.25`, cursor: 'pointer' }}
      id={`${TOP_LEVEL_ID}__${start}`}
      {...eventBoxProps}
      {...(duration > 30 && { alignItems: 'start', flexDirection: 'column' })}
      onClick={() => onScheduleItemClicked(externalId)}
    >
      <ChakraFlex alignItems="center">
        {isCancelled && isViewCancelledAppointmentsInMaudEnabled && (
          <Icon name={FAIconName.calendarXmarkRegular} color="critical.text" />
        )}
        {isNew ? (
          <Text
            id={`${TOP_LEVEL_ID}__${start}-type-v2`}
            variant="captionMedium"
          >
            {title}
          </Text>
        ) : (
          <ChakraText id={`${TOP_LEVEL_ID}__${start}-type`} {...labelBold}>
            {title}
          </ChakraText>
        )}
      </ChakraFlex>

      {isNew ? (
        <Text id={`${TOP_LEVEL_ID}__${start}--duration-v2`} variant="caption">
          {duration > 30 ? titleTimePart.replace(',', '') : titleTimePart}
        </Text>
      ) : (
        <ChakraText
          id={`${TOP_LEVEL_ID}__${start}-duration`}
          paddingTop="2px"
          {...label}
        >
          {duration > 30 ? titleTimePart.replace(',', '') : titleTimePart}
        </ChakraText>
      )}
    </ChakraFlex>
  );
};

export default ScheduleWeekEventItem;
